import { useState } from 'react';
import { Helmet } from 'react-helmet';

import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Modal } from 'antd';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import SuppliersReportModal from '@/components/Suppliers/SuppliersReportModal';
import {
  ListSuppliersQuery,
  SupplierReduced,
} from '@/core/domain/people/Suppliers/Suppliers.types';
import { useDeleteSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { PageProps } from '@/types/PageProps';
import SuppliersFilters from '@/components/Suppliers/SuppliersFilters';
import SuppliersList from '@/components/Suppliers/SuppliersList';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

const SuppliersPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [modalReportVisible, modalReportAction] = useDisclosure();

  const [queryParams, setQueryParams] = useState<ListSuppliersQuery>({
    order: 'ASC',
    ...convertParams<ListSuppliersQuery>({
      name: 'string',
      search: 'string',
      cpfCnpj: 'string',
      product: 'string',
      nickName: 'string',
      order: 'string',
      active: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  usePersistSearchParams(queryDebounce);

  const deleteSupplierMutation = useDeleteSupplier();

  const handleRemoveClick = (supplier: SupplierReduced) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'suppliers.remove',
      }),
      content: intl.formatMessage({
        id: 'suppliers.remove.modal.content',
      }),
      onOk: async () => {
        await deleteSupplierMutation.mutateAsync(supplier.id);
      },
      okType: 'danger',
      okText: intl.formatMessage({ id: 'commons.entity.remove' }),
    });
  };

  function toggleReportModal() {
    modalReportAction.toggle();
  }

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={toggleReportModal}
              disabled={
                !hasPermissions([
                  Authorities.REGISTER_SUPPLIER_REPORT_SUPPLIERS,
                ])
              }
            >
              {intl.formatMessage({
                id: 'suppliers.searchBar.generateReport',
              })}
            </Button>

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/registry/suppliers/create')}
              disabled={!hasPermissions([Authorities.REGISTER_SUPPLIER_ADD])}
            >
              {intl.formatMessage({ id: 'suppliers.searchBar.button' })}
            </Button>
          </SpaceForButton>
        }
      >
        <SuppliersFilters
          queryParams={queryParams}
          setQueryParams={setQueryParams}
        />

        <SuppliersList
          handleRemoveClick={handleRemoveClick}
          queryParams={queryDebounce}
        />

        <SuppliersReportModal
          visible={modalReportVisible}
          handleVisible={toggleReportModal}
        />
      </TabContentWrapper>
    </>
  );
};

export default SuppliersPage;
